/**
 * Assistant - Collapsible and resizable side panel with tab navigation.
 *
 * Contains AI Assistant and Activity tabs with toggle button when collapsed.
 * Manages panel state (collapsed/expanded), tab selection, and panel width.
 */

import { JSX, useCallback, useEffect, useRef, useState } from 'react';
import { BoltIcon, SparklesIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { Button } from '@heroui/react';

import { useUIStore } from '~/stores/uiStore';

import { ProjectActivity } from '../Activity';
import { ProjectChat } from '../Chat';

import { ProjectAssistantToggle } from './Toggle';

type TabId = 'chat' | 'progress';

// Panel width constraints
const MIN_PANEL_WIDTH = 320; // Minimum width in pixels
const MAX_PANEL_WIDTH = 800; // Maximum width in pixels

interface ProjectAssistantProps {
  className?: string;
  disabled?: boolean;
  existingPRD?: string;
  onSavePRD?: (prdContent: string) => void;
  onToggle?: (expanded: boolean) => void;
  projectDetails?: string;
  projectId?: string;
  projectName?: string;
}

interface TabConfig {
  id: TabId;
  label: string;
  icon: typeof SparklesIcon;
}

const TABS: TabConfig[] = [
  { id: 'chat', label: 'AI Assistant', icon: SparklesIcon },
  { id: 'progress', label: 'Activity', icon: BoltIcon },
];

/**
 * Tab button component.
 */
interface TabButtonProps {
  tab: TabConfig;
  active: boolean;
  onClick: () => void;
}

function TabButton({ active, onClick, tab }: TabButtonProps): JSX.Element {
  const Icon = tab.icon;

  return (
    <button
      aria-label={tab.label}
      aria-current={active ? 'page' : undefined}
      className={`
        flex items-center gap-2 px-4 py-2 text-sm font-medium
        border-b-2 transition-colors
        ${
          active
            ? 'border-primary text-foreground'
            : 'border-transparent text-muted hover:text-foreground hover:border-default'
        }
      `}
      onClick={onClick}
      type="button"
    >
      <Icon className="w-4 h-4" />
      <span>{tab.label}</span>
    </button>
  );
}

/**
 * Assistant component with tabs for AI Assistant and Activity.
 */
export function ProjectAssistant(props: ProjectAssistantProps): JSX.Element {
  const {
    className = '',
    disabled = false,
    existingPRD,
    onSavePRD,
    onToggle,
    projectDetails,
    projectId,
    projectName,
  } = props;
  const {
    activeProjectAssistantTab,
    isProjectAssistantExpanded,
    projectAssistantWidth,
    setActiveProjectAssistantTab,
    setProjectAssistantExpanded,
    setProjectAssistantWidth,
    toggleProjectAssistant,
  } = useUIStore();

  // Resize state
  const [isResizing, setIsResizing] = useState(false);
  const resizeRef = useRef<{ startWidth: number; startX: number } | null>(null);

  const handleToggle = useCallback(() => {
    if (disabled) {
      return;
    }
    toggleProjectAssistant();
    onToggle?.(!isProjectAssistantExpanded);
  }, [disabled, toggleProjectAssistant, onToggle, isProjectAssistantExpanded]);

  const handleClose = useCallback(() => {
    setProjectAssistantExpanded(false);
    onToggle?.(false);
  }, [setProjectAssistantExpanded, onToggle]);

  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape' && isProjectAssistantExpanded) {
        handleClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return (): void => document.removeEventListener('keydown', handleKeyDown);
  }, [isProjectAssistantExpanded, handleClose]);

  // Handle responsive behavior
  useEffect(() => {
    const handleResize = (): void => {
      // Auto-collapse on mobile
      if (window.innerWidth < 768 && isProjectAssistantExpanded) {
        setProjectAssistantExpanded(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return (): void => window.removeEventListener('resize', handleResize);
  }, [isProjectAssistantExpanded, setProjectAssistantExpanded]);

  // Handle resize drag
  const handleResizeMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      setIsResizing(true);
      resizeRef.current = {
        startX: e.clientX,
        startWidth: projectAssistantWidth,
      };
    },
    [projectAssistantWidth],
  );

  useEffect(() => {
    if (!isResizing) return;

    const handleMouseMove = (e: MouseEvent): void => {
      if (!resizeRef.current) return;

      // Calculate new width (dragging left increases width since panel is on right)
      const deltaX = resizeRef.current.startX - e.clientX;
      const newWidth = Math.min(
        MAX_PANEL_WIDTH,
        Math.max(MIN_PANEL_WIDTH, resizeRef.current.startWidth + deltaX),
      );
      setProjectAssistantWidth(newWidth);
    };

    const handleMouseUp = (): void => {
      setIsResizing(false);
      resizeRef.current = null;
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return (): void => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing, setProjectAssistantWidth]);

  // Show toggle button when collapsed
  if (!isProjectAssistantExpanded) {
    return <ProjectAssistantToggle onClick={handleToggle} disabled={disabled} />;
  }

  // Expanded panel with tabs
  return (
    <>
      {/* Subtle backdrop - only on mobile */}
      <div
        className={`
          fixed inset-0 bg-background bg-opacity-20 z-40 sm:hidden
          ${isProjectAssistantExpanded ? 'block' : 'hidden'}
        `}
        onClick={handleClose}
        aria-hidden="true"
      />

      {/* Overlay to capture mouse events during resize */}
      {isResizing && <div className="fixed inset-0 z-[60] cursor-col-resize" aria-hidden="true" />}

      {/* Side Panel */}
      <aside
        className={`
          fixed top-0 right-0 h-full z-50
          bg-surface
          shadow-xl
          border-l border-default
          transform transition-transform duration-300 ease-in-out
          ${isProjectAssistantExpanded ? 'translate-x-0' : 'translate-x-full'}
          ${className}
        `}
        style={{
          width: window.innerWidth < 640 ? '100%' : `${projectAssistantWidth}px`,
        }}
        aria-label="AI Companion"
      >
        {/* Resize Handle */}
        <button
          type="button"
          className="absolute left-0 top-0 bottom-0 w-1 cursor-col-resize hover:bg-primary/50 active:bg-primary transition-colors hidden sm:block border-0 bg-transparent p-0"
          onMouseDown={handleResizeMouseDown}
          aria-label="Resize panel"
        />

        <div className="flex bg-background h-full">
          <main className="flex-1 flex flex-col min-w-0">
            {/* Header with Tabs */}
            <header className="bg-surface-elevated border-b border-default shrink-0">
              <div className="px-4 py-3">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3 min-w-0 flex-1">
                    <SparklesIcon className="w-8 h-8" />
                    <div className="min-w-0 flex-1">
                      <h2 className="text-base font-semibold">AI Companion</h2>
                      {!!projectName && <p className="text-sm truncate">{projectName}</p>}
                    </div>
                  </div>
                  <Button
                    aria-label="Close Panel"
                    className="flex-shrink-0"
                    isIconOnly
                    onPress={handleClose}
                    size="sm"
                    title="Close Panel"
                    variant="light"
                  >
                    <XMarkIcon className="w-4 h-4 text-muted dark:text-muted" />
                  </Button>
                </div>

                {/* Tab Navigation */}
                <div className="flex gap-1 -mb-3">
                  {TABS.map(tab => (
                    <TabButton
                      key={tab.id}
                      tab={tab}
                      active={activeProjectAssistantTab === tab.id}
                      onClick={() => setActiveProjectAssistantTab(tab.id)}
                    />
                  ))}
                </div>
              </div>
            </header>

            {/* Tab Content */}
            <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
              <div
                className="flex-1 flex flex-col overflow-hidden"
                style={{
                  display: activeProjectAssistantTab === 'chat' ? 'flex' : 'none',
                }}
              >
                <ProjectChat
                  existingPRD={existingPRD}
                  projectId={projectId}
                  projectName={projectName}
                  projectDetails={projectDetails}
                  onSavePRD={onSavePRD}
                />
              </div>
              <div
                className="flex-1 flex flex-col overflow-hidden"
                style={{
                  display: activeProjectAssistantTab === 'progress' ? 'flex' : 'none',
                }}
              >
                <ProjectActivity />
              </div>
            </div>
          </main>
        </div>
      </aside>
    </>
  );
}

export default ProjectAssistant;
